// Phoenix Capital shared formatters — the single source of truth for
// date / time / relative / currency / number / phone presentation across
// BOTH the staff app and the external portals. These are PURE functions
// (no React, no auth context) so they're safe to import from either
// surface, including the isolated portal bundle via the design-system
// barrel.
//
// 🔴 ONLY THE STAFF SIDE HAS A REACT BINDING, AND THIS COMMENT USED TO SAY
// OTHERWISE. It named `@/portal/lib/datetime` as the portal's twin of
// `@/utils/datetime`. That module has never existed — a repo-wide grep for
// the path returns exactly one hit, this line — and neither does any portal
// timezone hook, because there is nothing for one to read: `PortalUser` has
// no timezone column and `PortalMeDto` no field. So a portal caller passes
// no zone and lands on `FALLBACK_TIMEZONE`, which is deliberate and is
// pinned; it is not a hook someone forgot to call. Do not send anyone
// looking for that module, and do not create it without the server column
// first — an empty binding would report Phoenix while implying a preference
// was consulted.

/** Default IANA timezone — mirrors the server `IUserTimeZoneResolver`. */
export const FALLBACK_TIMEZONE = "America/Phoenix";

/**
 * The zone the FIRM's day-boundary runs in — the client mirror of the server's
 * `OperationalDay.Zone` (`Core/Common/OperationalDay.cs`), which resolves to
 * `IUserTimeZoneResolver.DefaultIanaId`.
 *
 * 🔴 THIS IS NOT `FALLBACK_TIMEZONE`, AND THE FACT THAT THEY HOLD THE SAME
 * STRING IS WHY THEY MUST BE NAMED APART. `FALLBACK_TIMEZONE` answers "we were
 * given no viewer zone"; this answers "the viewer's zone is not the question".
 * Collapsing them would make a deliberate rule look like a default, and the
 * next person to give the fallback a different value would silently move every
 * operational deadline with it.
 *
 * WHY DEADLINES DO NOT FOLLOW THE VIEWER (owner-ruled 2026-09-10, the ONE CLOCK
 * ruling): a wall-calendar deadline is a fact about the firm, not about the
 * reader. The server computes every day-count from it — `daysDelinquent`,
 * overdue flags, fee assessment — on this zone and never per-viewer
 * (`IUserTimeZoneResolver` has zero injection sites). A countdown that followed
 * the reader would put two clocks on one screen: `VitalsStrip` renders the
 * server's `daysDelinquent` beside a client countdown of the same loan, so a
 * viewer outside this zone would read "Days delinquent: 0" next to "1 day
 * overdue". INSTANTS still follow the viewer — "submitted 3pm" is 3pm where you
 * are standing. The split is the class of the value, not the surface.
 */
export const OPERATIONAL_TIMEZONE = "America/Phoenix";

function parseInstant(iso: string | Date | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === "") return null;
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `Apr 25, 2026, 3:34 PM` by default, rendered in `tz`. */
export function formatDateTimeInTz(
  iso: string | Date | null | undefined,
  tz?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = parseInstant(iso);
  if (!d) return "";
  return d.toLocaleString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...opts,
  });
}

/** `Apr 25, 2026` by default. */
export function formatDateInTz(
  iso: string | Date | null | undefined,
  tz?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = parseInstant(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
    ...opts,
  });
}

/** `3:34 PM` by default. */
export function formatTimeInTz(
  iso: string | Date | null | undefined,
  tz?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = parseInstant(iso);
  if (!d) return "";
  return d.toLocaleTimeString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    ...opts,
  });
}

/**
 * Compact relative time (`just now`, `2 hours ago`, `3 days ago`). For
 * anything older than ~30 days it falls back to an absolute date in `tz`
 * so distant timestamps stay legible. Pair with `formatDateTimeInTz` for
 * an exact-on-hover `title`.
 */
export function formatRelativeTime(
  iso: string | Date | null | undefined,
  tz?: string | null,
  now: Date = new Date(),
): string {
  const d = parseInstant(iso);
  if (!d) return "";
  const diffMs = now.getTime() - d.getTime();
  const suffix = diffMs >= 0 ? "ago" : "from now";
  const sec = Math.round(Math.abs(diffMs) / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ${suffix}`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ${suffix}`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ${suffix}`;
  return formatDateInTz(d, tz);
}

/**
 * Calendar-date renderer (`Apr 25, 2026`) that ignores the viewer's
 * timezone — for fields that are conceptually a wall-calendar date
 * (due dates, effective dates) rather than an instant.
 */
export function formatCalendarDate(
  iso: string | Date | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = parseInstant(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
    ...opts,
  });
}

/**
 * `June 2026` (or `Jun 2026` when short) — the month a reporting period
 * belongs to, rendered in UTC.
 *
 * Period boundaries are stored as UTC midnight on the 1st, so the viewer's
 * timezone must never touch them: `2026-06-01T00:00:00Z` formatted in any
 * negative-offset zone lands on 31 May, which drags the label back a whole
 * month. That is the exact split this helper exists to close — the admin
 * tier read June periods as "May 2026" while the portal and every
 * notification said "June 2026".
 *
 * A period is always exactly one calendar month (see VendorProgramPeriods
 * server-side: MonthStart is the 1st at UTC midnight, PeriodEnd is the last
 * day of the same month), so the month a period "mostly falls in" is simply
 * its own month — no majority-of-days rule is needed, and none is possible
 * to need without changing the generator.
 *
 * Portal-safe: `design-system/lib/format` is on the portal import
 * allow-list, so both tiers derive the label here rather than each keeping
 * a local copy.
 */
export function formatPeriodMonth(
  iso: string | Date | null | undefined,
  variant: "long" | "short" = "long",
): string {
  return formatCalendarDate(iso, {
    month: variant,
    year: "numeric",
    day: undefined,
  });
}

/** Convert a `YYYY-MM-DD` `<input type="date">` value to a noon-UTC instant. */
export function calendarDateInputToIso(value: string): string | null {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  return `${value}T12:00:00.000Z`;
}

/**
 * Builds a UTC-midnight `Date`, with `monthIndex` / `day` allowed to overflow
 * their range the way `Date.UTC` already handles.
 *
 * `Date.UTC` remaps a two-digit year onto 19xx, and this app does carry a
 * year-0001 deferred-due-date sentinel (see `isDeferredDueDate`), so such a year
 * is shifted out of that window by a whole Gregorian cycle — 400 years, which is
 * leap-identical — and shifted back afterwards. Reading the year back off the
 * RESULT rather than off the argument keeps it exact when `day` rolls the date
 * into another year.
 */
function utcMidnight(year: number, monthIndex: number, day: number): Date {
  const shift = year >= 0 && year <= 99 ? 400 : 0;
  const d = new Date(Date.UTC(year + shift, monthIndex, day));
  if (shift) d.setUTCFullYear(d.getUTCFullYear() - shift);
  return d;
}

/**
 * A wall-calendar date plus `months`, computed entirely in UTC. Returns a
 * UTC-midnight `Date`, or `null` when the input is missing or unparseable.
 *
 * 🔴 THE ARITHMETIC IS THE DEFECT, NOT THE FORMATTING. `Date.prototype.setMonth`
 * and `setDate` are LOCAL-time mutators: they read and write the wall clock of
 * the BROWSER's zone. A wall-calendar value is stored at UTC midnight, so in any
 * negative-offset zone the instant already reads as the PREVIOUS day locally —
 * and the month step then anchors on the wrong day-of-month before any renderer
 * is involved. Measured on `2026-03-01T00:00:00Z` + 1 month, rendered in UTC:
 * `Mar 29` in America/Phoenix and `Mar 28` in America/New_York, where `Apr 1` is
 * correct. Swapping the renderer fixes nothing; the value is already wrong.
 *
 * 🔴 THE DAY-OF-MONTH IS CLAMPED, AND `setUTCMonth` IS NOT A SIMPLIFICATION OF
 * THIS. `setUTCMonth` OVERFLOWS where .NET clamps — `2026-01-31` + 1 month is
 * `2026-03-03` under `setUTCMonth` and `2026-02-28` under `DateTime.AddMonths`
 * (`dotnet/runtime`, `System/DateTime.cs`: `if (d > days) d = days;`, which
 * `DateOnly.AddMonths` delegates to). Every server-side due date in this product
 * is computed with `AddMonths`, so a client preview that overflows disagrees
 * with the row the server will write on every 29th, 30th and 31st — and passes a
 * test built on any other day of the month.
 */
export function addCalendarMonths(
  iso: string | Date | null | undefined,
  months: number,
): Date | null {
  const d = parseInstant(iso);
  if (!d) return null;
  const total = d.getUTCMonth() + months;
  const year = d.getUTCFullYear() + Math.floor(total / 12);
  const monthIndex = ((total % 12) + 12) % 12;
  // Day 0 of the following month is the last day of this one.
  const lastDayOfTarget = utcMidnight(year, monthIndex + 1, 0).getUTCDate();
  return utcMidnight(year, monthIndex, Math.min(d.getUTCDate(), lastDayOfTarget));
}

/**
 * A wall-calendar date plus `days`, computed entirely in UTC. Returns a
 * UTC-midnight `Date`, or `null` when the input is missing or unparseable.
 *
 * Same class as `addCalendarMonths` above — `setDate` is a local mutator, so a
 * week added across a DST transition lands a day early once the result is read
 * back in UTC. Measured on a due date of `2026-03-25T00:00:00Z` + 7 days: a
 * Europe/London or Europe/Berlin browser produces `2026-03-31` where `2026-04-01`
 * is correct, i.e. it crosses a MONTH boundary; Australia/Sydney does the same at
 * its own October transition. There is no clamping question here — a day count
 * has no day-of-month to lose.
 *
 * The result is an instant, so render it with `formatCalendarDate` or read it
 * back with `isoDateInTz(d, "UTC")`. Never with a local getter.
 */
export function addCalendarDays(
  iso: string | Date | null | undefined,
  days: number,
): Date | null {
  const d = parseInstant(iso);
  if (!d) return null;
  return utcMidnight(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days);
}

const PACIFIC_TZ = "America/Los_Angeles";

/** A LeadGen pay week rendered as a full 7-day range, UTC + Pacific. */
export interface PayWeekRange {
  /** Headline calendar range, e.g. `Jul 13 – Jul 19, 2026` (UTC). */
  rangeLabel: string;
  /** `Mon Jul 13 12:00 AM → Sun Jul 19 11:59 PM` (UTC). */
  utcLine: string;
  /** `Sun Jul 12 5:00 PM → Sun Jul 19 4:59 PM` (Pacific). */
  pacificLine: string;
  /** `PDT` | `PST` — the Pacific abbreviation at the week's start (DST-correct). */
  pacificAbbrev: string;
}

/** `Mon Jul 13 12:00 AM` — weekday + short date + time of an instant in `tz`. */
function weekdayDateTime(d: Date, tz: string): string {
  const weekday = d.toLocaleDateString("en-US", { timeZone: tz, weekday: "short" });
  const monthDay = d.toLocaleDateString("en-US", { timeZone: tz, month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" });
  return `${weekday} ${monthDay} ${time}`;
}

/** The short timezone abbreviation observed at `d` in `tz` (e.g. `PDT` / `PST`). */
function tzAbbrev(d: Date, tz: string): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
    .formatToParts(d)
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? "";
}

/**
 * Render a LeadGen pay week (its `weekOf` anchor is Monday 00:00 UTC) as the full
 * 7-day Mon→Sun range in BOTH UTC and Pacific. DISPLAY-ONLY — the pay-week boundary
 * itself is unchanged. Pacific is DST-correct automatically via `Intl`: a summer
 * week renders PDT (−7), a winter week PST (−8). Returns null for a blank input.
 *
 * @param weekOfIso the server-resolved `summary.weekOf` (Monday 00:00 UTC).
 */
export function formatPayWeekRange(
  weekOfIso: string | Date | null | undefined,
): PayWeekRange | null {
  const start = parseInstant(weekOfIso);
  if (!start) return null;

  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const endExclusive = new Date(start.getTime() + WEEK_MS); // next Monday 00:00 UTC
  const endInstant = new Date(endExclusive.getTime() - 60 * 1000); // Sunday 23:59 UTC

  // Headline dates are wall-calendar (UTC): start month/day, end month/day/year.
  const startMd = start.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
  const endMdY = endInstant.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return {
    rangeLabel: `${startMd} – ${endMdY}`,
    utcLine: `${weekdayDateTime(start, "UTC")} → ${weekdayDateTime(endInstant, "UTC")}`,
    pacificLine: `${weekdayDateTime(start, PACIFIC_TZ)} → ${weekdayDateTime(endInstant, PACIFIC_TZ)}`,
    pacificAbbrev: tzAbbrev(start, PACIFIC_TZ),
  };
}

/** ISO 8601 calendar-date (`2026-04-25`) of the instant as observed in `tz`. */
export function isoDateInTz(
  iso: string | Date | null | undefined,
  tz?: string | null,
): string {
  const d = parseInstant(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-CA", {
    timeZone: tz || FALLBACK_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * Whole CALENDAR DAYS from today until a wall-calendar date. Negative is
 * overdue, `0` is due today, `null` when the value is missing or unparseable.
 *
 * 🔴 THIS IS NOT AN ELAPSED-TIME MEASURE, AND THAT IS THE WHOLE POINT. The
 * shape it replaces — `Math.round((due - Date.now()) / 86_400_000)` — answers
 * "how many 24-hour spans away is that instant", which is a different question
 * and gives a different answer for most of the working day. A due date stored
 * at UTC midnight is 7 hours in the past by 5am in Phoenix (UTC-7), so the old
 * form read `1 day overdue` on the very morning something was due, every day,
 * for every user. Anchoring both sides to their own calendar day removes the
 * time of day from the arithmetic entirely.
 *
 * The two sides are read in DIFFERENT zones, deliberately:
 *  - the DUE date in **UTC**, because it is a wall-calendar value — a day
 *    somebody picked, widened server-side from a `DateOnly`. Reading it in the
 *    viewer's zone shifts it back a day for anyone west of UTC.
 *  - **today** in `OPERATIONAL_TIMEZONE`, because a deadline's day is a fact
 *    about the FIRM rather than about the reader. This paragraph used to say
 *    "the VIEWER's zone, because that is a real instant and 'what day is it' is
 *    a question about where the viewer is standing" — true of an instant, and
 *    the ONE CLOCK ruling (owner-ruled 2026-09-10) settled that a countdown is
 *    not one. The code moved and this line did not, so for a while the comment
 *    argued for exactly the viewer-zone read the ruling had just removed. See
 *    `OPERATIONAL_TIMEZONE` for why a per-viewer countdown puts two clocks on
 *    one card.
 *
 * Time-of-day agnostic within the day on both sides, so it is unaffected by the
 * stored population being mixed between UTC midnight (legacy) and noon UTC
 * (what `calendarDateInputToIso` writes) — both land on the same UTC day.
 *
 * `now` is injectable so a countdown can be tested without freezing the clock.
 */
export function calendarDaysUntil(
  iso: string | Date | null | undefined,
  now: Date = new Date(),
): number | null {
  const dueDay = isoDateInTz(iso, "UTC");
  if (!dueDay) return null;
  const todayDay = isoDateInTz(now, OPERATIONAL_TIMEZONE);
  if (!todayDay) return null;

  const dueMs = Date.parse(`${dueDay}T00:00:00Z`);
  const nowMs = Date.parse(`${todayDay}T00:00:00Z`);
  if (Number.isNaN(dueMs) || Number.isNaN(nowMs)) return null;

  // Both operands are UTC midnights, so the quotient is already a whole
  // number; the round is belt-and-braces, not a correction.
  return Math.round((dueMs - nowMs) / 86_400_000);
}

/**
 * Today on the FIRM's clock, as `YYYY-MM-DD` — the value a date input's `min`
 * or `max` bound wants. The client mirror of the server's
 * `OperationalDay.Today()`.
 *
 * 🔴 A BOUND IS NOT A SEED, AND THEY TAKE DIFFERENT CLOCKS. A seeded VALUE is a
 * convenience — "what day is it where I am standing" — so it follows the viewer
 * (`isoDateInTz(new Date(), useUserTimeZone())`, which is what
 * `todaySeedsInViewerZone` pins). A BOUND is a validation constraint, and its
 * only job is to agree with the server rule it mirrors. Every such rule in this
 * product reads the operational day:
 * `LoanTransitionService:321` (`OperationalDay.Today()`) for a tender date, and
 * `CollectionPackageService.UpdateDueDateAsync` (`PlatformDisplayZone.DayOf`,
 * which is the SAME `TimeZoneInfo` — `OperationalDay.Zone` returns
 * `PlatformDisplayZone.Platform`) for a package due date.
 *
 * 🔴 WHY THIS MATTERS MORE THAN A MISLABEL: a bound REFUSES INPUT. When
 * `TransitionStartWizard` read its gate on this clock and its `min` on the UTC
 * day, the form accepted a day its own picker would not offer — measured at
 * `2026-09-04T01:00:00Z` (18:00 Phoenix, Sep 3): the gate returned 0 for
 * `2026-09-03` while `min` was `2026-09-04`, on an IRREVOCABLE prepayment
 * notice. Seeding the wrong day is a bad default; bounding the wrong day is a
 * locked door.
 *
 * `now` is injectable so a bound is testable without faking the global clock.
 */
export function operationalToday(now: Date = new Date()): string {
  return isoDateInTz(now, OPERATIONAL_TIMEZONE);
}

/**
 * THE THREE CALENDAR-COMPARISON PREDICATES.
 *
 * Every one is a thin reading of `calendarDaysUntil`, deliberately — never a
 * second implementation. That function already resolves the hard part (the due
 * side in UTC because it is a wall-calendar day, today on the OPERATIONAL day
 * because overdue-ness is a fact about the firm rather than about the reader —
 * this said "the VIEWER's zone" until the ONE CLOCK ruling, and `calendarDaysUntil`
 * now takes no zone argument at all), and it is time-of-day agnostic on both sides,
 * which is why one rule covers a value stored at UTC midnight and one stored at
 * noon UTC without branching.
 *
 * 🔴 WHY THREE AND NOT ONE. Eight surfaces wrote this comparison independently
 * and they do NOT share a signature — `day < now`, `day >= now && day <= N`,
 * and `day <= now`. Collapsing them into a single `isPastCalendarDay` leaves the
 * window shape unfixed and inverts the inclusive one: "has this taken effect"
 * is TRUE on its own effective day, where "is this overdue" is FALSE on its own
 * due day. The boundary day belongs to opposite sides of the two questions, so
 * one predicate cannot answer both.
 *
 * 🔴 EVERY ONE FAILS CLOSED ON A MISSING OR UNPARSEABLE VALUE, and each fails
 * closed in its OWN direction rather than sharing a default:
 *   - nothing is overdue if there is no deadline
 *   - nothing is due soon if there is no due date
 *   - nothing has taken effect if there is no start date
 * A shared `?? false` would be wrong for at least one of them under any reading;
 * making the absence explicit per predicate is what keeps that visible.
 *
 * `now` stays injectable on all three so a caller can be tested without
 * freezing the clock, matching `calendarDaysUntil`.
 */

/**
 * The day named by `iso` is strictly BEFORE the viewer's today.
 *
 * A deadline falling ON the current day is NOT past — that is the whole
 * defect this replaces. The shape it supersedes compared a bare `YYYY-MM-DD`
 * against `new Date().toISOString().slice(0, 10)`, i.e. the UTC day, so from
 * 17:00 Phoenix a row due TODAY was painted overdue for the remaining seven
 * hours of the day it was actually due.
 */
export function isPastCalendarDay(
  iso: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  const days = calendarDaysUntil(iso, now);
  return days !== null && days < 0;
}

/**
 * The day named by `iso` falls in the window `[today, today + withinDays]`.
 *
 * INCLUSIVE at both ends. Due TODAY is due soon — excluding it is the F40
 * defect, where a "due soon" count could not see the one item most needing
 * attention. Something already overdue is NOT "due soon"; it is overdue, which
 * is `isPastCalendarDay`'s question.
 */
export function isWithinCalendarDays(
  iso: string | Date | null | undefined,
  withinDays: number,
  now: Date = new Date(),
): boolean {
  const days = calendarDaysUntil(iso, now);
  return days !== null && days >= 0 && days <= withinDays;
}

/**
 * The day named by `iso` is today or earlier — an effective date that has
 * arrived.
 *
 * 🔴 INCLUSIVE, and that is the inverse of `isPastCalendarDay` rather than its
 * negation. A fee schedule effective from the 9th IS in force on the 9th. The
 * shape this replaces compared an instant against a UTC-midnight calendar
 * value, so it went the other way at both of the boundaries it meets: a
 * midnight-stored row took effect seven hours early in Phoenix, and a
 * noon-stored one took effect five hours LATE. One rule covers both, which is
 * why the two sites can share it despite storing on different conventions.
 */
export function hasCalendarDayArrived(
  iso: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  const days = calendarDaysUntil(iso, now);
  return days !== null && days <= 0;
}

/** `$42,000` — whole dollars, no cents. */
export function formatCurrency(
  amount: number | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    ...opts,
  }).format(amount);
}

/**
 * The magnitude at or above which a stat-tile currency value abbreviates.
 * Taken from the Table & Tile Standard's own worked examples, which render
 * `1,284` in full and compact `12.9K` — so a vendor's ordinary invoice
 * figures stay exact and only genuinely large ones shorten.
 */
const COMPACT_CURRENCY_THRESHOLD = 10_000;

/**
 * `$1,000.00` / `$12.9K` / `$4.2M` — currency for a NARROW stat tile, where a
 * clipped figure is worse than an approximate one.
 *
 * Below the threshold the exact amount renders with cents; at or above it the
 * value abbreviates rather than growing past its column. Use this only where
 * the container is genuinely tight — prose, ledgers and anything a reader
 * might reconcile against should keep `formatCurrency`, because an
 * abbreviated figure cannot be checked against a bank statement.
 *
 * Keyed on MAGNITUDE, so a large negative abbreviates too (`-$12.9K`).
 */
export function formatCompactCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "";
  return Math.abs(amount) < COMPACT_CURRENCY_THRESHOLD
    ? formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : formatCurrency(amount, {
        notation: "compact",
        // `minimumFractionDigits: 0` is load-bearing, not tidying: compact
        // notation otherwise pads a round value to `$10.0K`, and the trailing
        // zero is pure noise in a tile whose whole problem was width.
        maximumFractionDigits: 1,
        minimumFractionDigits: 0,
      });
}

/** `1,234` — grouped integer. */
export function formatNumber(
  value: number | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return new Intl.NumberFormat("en-US", opts).format(value);
}

/**
 * Progress percentage for payoff / amortization bars. A `0..1` fraction is
 * rendered as a whole-number percent at or above 1%, but with TWO decimals
 * below 1% so a real-but-tiny amount of progress never collapses to a
 * misleading "0%" (a $5,531 principal paydown on a $4.2M loan reads "0.13%").
 * A genuinely zero fraction still reads "0%"; any nonzero value under 1% is
 * floored to "0.01%", never "0.00%". Clamped to [0, 1]; non-finite input
 * reads "0%".
 */
export function formatProgressPercent(fraction: number): string {
  const p = Number.isFinite(fraction) ? Math.min(100, Math.max(0, fraction * 100)) : 0;
  if (p <= 0) return "0%";
  if (p < 1) return `${Math.max(0.01, Math.round(p * 100) / 100).toFixed(2)}%`;
  return `${Math.round(p)}%`;
}

/**
 * Deal-import masks a missing counterparty email as
 * `unknown-{guid:N}@example.local` (DealImportService). These are storage
 * placeholders, never real addresses — display surfaces render them as a
 * muted "No email on file" instead of leaking the synthetic value. Stored
 * data is unchanged; this is display-only.
 */
const PLACEHOLDER_EMAIL_RE = /^unknown-[0-9a-f]{32}@example\.local$/i;

export function isPlaceholderEmail(email: string | null | undefined): boolean {
  return !!email && PLACEHOLDER_EMAIL_RE.test(email.trim());
}

/** The standard placeholder-email display copy. */
export const NO_EMAIL_ON_FILE = "No email on file";

/**
 * Display form of a counterparty email: the address itself, or null when
 * it's missing or an import placeholder (callers choose their own empty
 * rendering — "—", muted copy, fall-through to phone, …).
 */
export function displayEmail(email: string | null | undefined): string | null {
  if (!email || isPlaceholderEmail(email)) return null;
  return email;
}

/**
 * Light-touch US phone formatter: `(602) 555-0142`. Leaves anything that
 * isn't a clean 10- or 11-digit number untouched so international / partial
 * inputs survive a round-trip.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return raw;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

/** Truncate to `max` chars with an ellipsis. Pair with a `title` for the full value. */
export function truncate(value: string | null | undefined, max = 48): string {
  if (!value) return "";
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
